import type { BenefitSummary } from "@yutai/shared"
import { combinedYield, minAcquisitionAmount } from "../features/benefit/filterBenefits.js"
import { formatMarketCap } from "../lib/formatMarketCap.js"
import { formatDeliveryMonths } from "../lib/formatMonths.js"
import { MARKET_CAP_TIER_LABELS, marketCapTierFromValue } from "../lib/marketCapTier.js"
import { RELEVANCE_LEVEL_LABELS, relevanceLevelFromScore } from "../lib/relevance.js"
import styles from "./BenefitSummaryStats.module.css"

interface Props {
  benefit: BenefitSummary
}

export function BenefitSummaryStats({ benefit }: Props) {
  const monthsLabel = benefit.recordMonths.map((m) => `${m}月`).join("・")
  const deliveryMonthsLabel =
    benefit.deliveryMonths.length > 0 ? formatDeliveryMonths(benefit.deliveryMonths) : null
  const relevanceLevel = relevanceLevelFromScore(benefit.benefitRelevanceScore)
  const combined = combinedYield(benefit)
  const capTier = marketCapTierFromValue(benefit.marketCap)
  const minAcquisition = minAcquisitionAmount(benefit)

  const hasAcquisitionOrCap = minAcquisition != null || benefit.marketCap != null
  const hasYields =
    benefit.dividendYield != null || benefit.benefitYield != null || combined != null
  const hasNotes = benefit.holdingYears.length > 0 || relevanceLevel

  return (
    <div className={styles.root}>
      <div className={styles.statGrid}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>権利確定</span>
          <span className={styles.statValue}>{monthsLabel}</span>
        </div>
        {deliveryMonthsLabel && (
          <div className={styles.stat}>
            <span className={styles.statLabel}>送付月</span>
            <span className={styles.statValue}>{deliveryMonthsLabel}</span>
          </div>
        )}
      </div>

      {hasAcquisitionOrCap && (
        <div className={styles.statGrid}>
          {minAcquisition != null && (
            <div
              className={styles.stat}
              title="最低必要株数を保有するのに必要な概算投資額（事業年度末時点の株価水準に基づく）"
            >
              <span className={styles.statLabel}>最低取得額</span>
              <span className={styles.statValue}>
                {Math.round(minAcquisition).toLocaleString()}円
              </span>
            </div>
          )}
          {benefit.marketCap != null && (
            <div className={styles.stat}>
              <span className={styles.statLabel}>時価総額</span>
              <span className={styles.statValue}>
                {capTier && MARKET_CAP_TIER_LABELS[capTier]}{" "}
                <span className={styles.statValueSub}>({formatMarketCap(benefit.marketCap)})</span>
              </span>
            </div>
          )}
        </div>
      )}

      {hasYields && (
        <div className={styles.statGrid}>
          {benefit.dividendYield != null && (
            <div className={styles.stat}>
              <span className={styles.statLabel}>配当利回り</span>
              <span className={styles.statValue}>{benefit.dividendYield.toFixed(1)}%</span>
            </div>
          )}
          {benefit.benefitYield != null && (
            <div className={styles.stat}>
              <span className={styles.statLabel}>優待利回り</span>
              <span className={styles.statValue}>{benefit.benefitYield.toFixed(1)}%</span>
            </div>
          )}
          {combined != null && (
            <div
              className={styles.stat}
              title="最低必要株数を保有した場合の、事業年度末時点の株価水準に基づく概算値"
            >
              <span className={styles.statLabel}>総合利回り</span>
              <span className={styles.statValue}>{combined.toFixed(1)}%</span>
            </div>
          )}
        </div>
      )}

      {hasNotes && (
        <div className={styles.noteRow}>
          {benefit.holdingYears.length > 0 && (
            <span className={styles.longTermNote}>長期保有特典あり</span>
          )}
          {relevanceLevel && (
            <span className={styles.relevanceNote}>
              事業関連度: {RELEVANCE_LEVEL_LABELS[relevanceLevel]}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
